import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useTabsState } from './useTabsState';
import { Tab } from './Tab';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { FileTab } from '@/types';
import '../../styles/tabs.css';
import { useWorkspaceStore } from '@/stores/workspace';


export const TabsBar: React.FC = () => {
  const {
    addTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    pinTab,
    unpinTab,
    reorderTabs,
    selectTab,
    toggleTabSelection,
    createGroup,
    ungroup,
    expandGroup,
  } = useTabsState();

  const {
    tabs,
    activeTabId,
    selectedTabIds,
    groups,
  } = useWorkspaceStore(state => ({
    tabs: state.tabs,
    activeTabId: state.openFilePath,
    selectedTabIds: state.selectedTabIds,
    groups: state.groups,
  }));

  const pinnedTabs = useMemo(() => tabs.filter(t => t.isPinned), [tabs]);
  const unpinnedTabs = useMemo(() => tabs.filter(t => !t.isPinned), [tabs]);

  const [draggedTabId, setDraggedTabId] = useState<string | null>(null);
  const [contextMenuTab, setContextMenuTab] = useState<string | null>(null);
  const [wrappingEnabled, setWrappingEnabled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragOverIndexRef = useRef<number | null>(null);

  // Memoize tab components to prevent unnecessary re-renders
  const pinnedTabElements = useMemo(() => 
    pinnedTabs.map((tab, index) => (
      <Tab
        key={tab.id}
        index={index}
        tab={tab}
        isActive={activeTabId === tab.id}
        isSelected={selectedTabIds.includes(tab.id)}
        isDragging={draggedTabId === tab.id}
        onActivate={() => selectTab(tab.id)}
        onClose={() => closeTab(tab.id)}
        onContextMenu={() => setContextMenuTab(tab.id)}
        onDragStart={() => setDraggedTabId(tab.id)}
        onDragEnd={() => setDraggedTabId(null)}
        onReorder={(fromIndex, toIndex) => reorderTabs(fromIndex, toIndex)}
      />
    )),
    [pinnedTabs, activeTabId, selectedTabIds, draggedTabId]
  );

  const unpinnedTabElements = useMemo(() =>
    unpinnedTabs.map((tab, index) => (
      <Tab
        key={tab.id}
        index={index}
        tab={tab}
        isActive={activeTabId === tab.id}
        isSelected={selectedTabIds.includes(tab.id)}
        isDragging={draggedTabId === tab.id}
        onActivate={() => selectTab(tab.id)}
        onClose={() => closeTab(tab.id)}
        onContextMenu={() => setContextMenuTab(tab.id)}
        onDragStart={() => setDraggedTabId(tab.id)}
        onDragEnd={() => {
          setDraggedTabId(null);
          dragOverIndexRef.current = null;
        }}
        onReorder={(fromIndex, toIndex) => reorderTabs(fromIndex, toIndex)}
      />
    )),
    [unpinnedTabs, activeTabId, selectedTabIds, draggedTabId, pinnedTabs.length]
  );

  // Auto-scroll active tab into view
  useEffect(() => {
    if (activeTabId && scrollContainerRef.current) {
      const activeTab = scrollContainerRef.current.querySelector(
        `[data-tab-id="${activeTabId}"]`
      );
      if (activeTab) {
        activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }, [activeTabId]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'w') {
          // Ctrl+W: Close active tab
          e.preventDefault();
          if (activeTabId) {
            closeTab(activeTabId);
          }
        } else if (e.key === 'Tab') {
          // Ctrl+Tab: Switch to next tab
          e.preventDefault();
          const currentIndex = tabs.findIndex(t => t.id === activeTabId);
          const nextIndex = (currentIndex + 1) % tabs.length;
          selectTab(tabs[nextIndex].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tabs, activeTabId, closeTab, selectTab]);

  const handleBatchClose = useCallback(() => {
    selectedTabIds.forEach(tabId => closeTab(tabId));
  }, [selectedTabIds, closeTab]);

  return (
    <div className={`tabs-container ${wrappingEnabled ? 'wrapping' : 'scrolling'}`}>
      <div className="tabs-bar" role="tablist">
        {pinnedTabElements.length > 0 && (
          <div className="tabs-pinned">
            {pinnedTabElements}
            <div className="tabs-separator" />
          </div>
        )}

        <div 
          className="tabs-unpinned-container" 
          ref={scrollContainerRef}
        >
          {unpinnedTabElements}
        </div>
      </div>

      {contextMenuTab && (
        <ContextMenu
          tab={tabs.find(t => t.id === contextMenuTab)!}
          isGrouped={groups.some(g => g.tabIds.includes(contextMenuTab))}
          onClose={() => setContextMenuTab(null)}
          onCloseTab={() => {
            closeTab(contextMenuTab);
            setContextMenuTab(null);
          }}
          onCloseOthers={() => {
            closeOtherTabs(contextMenuTab);
            setContextMenuTab(null);
          }}
          onCloseAll={() => {
            closeAllTabs();
            setContextMenuTab(null);
          }}
          onPin={() => {
            pinTab(contextMenuTab);
            setContextMenuTab(null);
          }}
        />
      )}

      {selectedTabIds.length > 1 && (
        <div className="batch-actions">
          <button onClick={handleBatchClose}>
            Close {selectedTabIds.length} tabs
          </button>
        </div>
      )}
    </div>
  );
};